import axios, { AxiosInstance, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import { useNullableTokens } from '../providers/tokens-provider';
import { isApiError } from '../util';

type RefreshResponse = {
    data: {
        accessToken: string;
    };
};

const api: AxiosInstance = axios.create({
    timeout: 30000 // 30 seconds
});
axiosRetry(api, {
    retries: 3,
    shouldResetTimeout: true,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
        // if retry condition is not specified, by default idempotent requests are retried
        if (!error.response) {
            return true;
        }
        return error.response.status >= 500;
    }
});

interface UseRefreshApiOptions {
    baseUrl: string;
    refreshToken: string;
    refreshUrl: string;
}

interface UseRefreshApiResponse {
    refreshTokenApi: () => Promise<string>;
}

let refreshPromise: Promise<string> | null = null;

function useRefreshApi({
    baseUrl,
    refreshToken,
    refreshUrl
}: UseRefreshApiOptions): UseRefreshApiResponse {
    const { setAccessToken, deleteTokens } = useNullableTokens();

    const refreshTokenApi = (): Promise<string> => {
        if (refreshPromise === null) {
            const url = refreshUrl;

            refreshPromise = api
                .post(url, { refreshToken }, { baseURL: baseUrl })
                .then((response: AxiosResponse<RefreshResponse>) => {
                    setAccessToken(response.data.data.accessToken);

                    refreshPromise = null;

                    return response.data.data.accessToken;
                })
                .catch((error: unknown) => {
                    if (
                        isApiError(error, 'UNAUTHORISED') ||
                        isApiError(error, 'USER_NOT_FOUND')
                    ) {
                        deleteTokens();
                    }

                    refreshPromise = null;

                    throw error;
                });
        }
        return refreshPromise;
    };

    return { refreshTokenApi };
}

export { useRefreshApi };
